import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TABLES } from '../../../../../Apis/Tables';
import { MODELS } from '../../../../../Apis/Models';
import { clearSearchFilters } from '../../../../../reduxToolkit/Search/SearchSlice';
import { EmailFrequency } from '../../../../../enums/EmailFrecuency';
import {permissionObj} from "../../../../../Helpers/permission";
import {
    ATTENDANCE_SUBVIEW_MODEL,
    TABS_ATTENDANCE
} from "../Model/Model";
import {useLocation, useNavigate} from 'react-router-dom';
import {VIEW_TYPE} from '../../../../../enums/ViewType';
import {SPECIAL_PERMISSION_TAB} from "../Manage/SpecialPermission/Model/Model";
import routes from "../../../../../Routes/routes";
import {DEDUCTION_POLICY_TAB} from "../Manage/DeductionPolicy/Model/Model";

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage payroll/attendance module functionality.
 *
 * Handles:
 * - Tab management based on permissions
 * - Context initialization
 * - Filtering, exporting, and simple search
 * - State management for module variables like view, color, steps, and active tab
 *
 *
 */
const PayrollFunctionality = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const currentTab = location?.state?.currentTab;
    const currentSubView = location?.state?.currentSubView;
    const currentSubViewChild = location?.state?.currentSubViewChild;
    const actionsRef = useRef({});
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const [subView, setSubView] = useState(null); // Variable to manage subviews and permissions. When entering the module, it is necessary to initialize the variable (if applicable, used SUB_VIEW_MODEL).
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const [finalArray, setFinalArray] = useState([]);
    const [selectedOption, setSelectedOption] = useState(EmailFrequency.DAILY.id);
    const [bodyForExport, setBodyForExport] = useState('');
    const [searchElement, setSearchElement] = useState('');
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [search, setSearch] = useState(false);
    const [searchClean, setSearchClean] = useState(false);
    const [modified, setModified] = useState(false);
    const [criteria, setCriteria] = useState();
    const [sendSimpleSearch, setSendSimpleSearch] = useState(false);
    const contractorId = sessionStorage.getItem('contractorId');
    const supplierId = sessionStorage.getItem('providerId');
    const [render, setRender] = useState('');
    const [activeStep, setActiveStep] = useState(null);
    const [steps, setSteps] = useState([]);
    const [selectForDelete, setSelectForDelete] = useState([]);
    const [view, setView] = useState(VIEW_TYPE.LIST.name);
    const [moduleId, setModuleId] = useState(null);
    const [option, setOption] = useState(null);
    const [selectedList, setSelectedList] = useState([]);
    const [actionsMap, setActionsMap] = useState({});
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [subViewChild, setSubViewChild] = useState(null);
    const [exportReady, setExportReady] = React.useState(false);

    const MANAGE_SUBVIEWS_BY_PERMISSION = [
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS,
            permission: permissionObj.WEB_ATTENDANCE_SHIFT_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SPECIAL_PERMISSION,
            permission: permissionObj.WEB_USER_SPECIAL_PERMISSION_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SETTINGS,
            permission: permissionObj.WEB_ATTENDANCE_SETTING_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES,
            permission: permissionObj.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY,
            permission: permissionObj.WEB_PAYROLL_DEDUCTION_POLICY_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY,
            permission: permissionObj.WEB_PAYROLL_DEDUCTION_POLICY_USER_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.GEOFENCE,
            permission: permissionObj.WEB_ATTENDANCE_MANAGE_GEOFENCES_READ,
        },
    ];

    // Maps Submissions sub-views to the required permissions.
    const SUBMISSIONS_SUBVIEWS_BY_PERMISSION = [
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW,
            permission: permissionObj.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED,
            permission: permissionObj.WEB_ATTENDANCE_SUBMISSION_REQUEST_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY,
            permission: permissionObj.WEB_ATTENDANCE_SUBMISSION_HISTORY_READ,
        },
    ];

    useLayoutEffect(() => {
        if(currentTab != null && currentSubView !== undefined){
            setActiveStep(currentTab);
            setSubView(currentSubView);
            setSelectedOption(EmailFrequency.DAILY.id);
            handleStepChange(currentTab);
            if(currentSubViewChild) setSubViewChild(currentSubViewChild);
        }
    }, [location]);

    useEffect(() => {
        return () => {
            dispatch(clearSearchFilters());
        };
    }, []);

    /**
     * Hook to display the tabs if the user has the corresponding permissions.
     */
    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            {
                tab: TABS_ATTENDANCE.ATTENDANCE.TRANS,
                perm: [permissionObj?.WEB_ATTENDANCE_READ],
                index: TABS_ATTENDANCE.ATTENDANCE.INDEX,
            },
           {
                tab: TABS_ATTENDANCE.SUBMISSIONS.TRANS,
                perm:  [
                    permissionObj?.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_READ,
                    permissionObj?.WEB_ATTENDANCE_SUBMISSION_REQUEST_READ,
                    permissionObj?.WEB_ATTENDANCE_SUBMISSION_HISTORY_READ,
                ],
                index: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
            },
            {
                tab: TABS_ATTENDANCE.MANAGE.TRANS,
                perm: [
                    permissionObj?.WEB_ATTENDANCE_SHIFT_READ,
                    permissionObj?.WEB_USER_SPECIAL_PERMISSION_READ,
                    permissionObj?.WEB_ATTENDANCE_SETTING_READ,
                    permissionObj?.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
                    permissionObj?.WEB_ATTENDANCE_MANAGE_GEOFENCES_READ,
                    permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_READ,
                    permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_READ,
                ],
                index: TABS_ATTENDANCE.MANAGE.INDEX,
            },
            {
                tab: TABS_ATTENDANCE.OVERTIME_POLICIES.TRANS,
                perm: [permissionObj?.WEB_PAYROLL_POLICY_READ],
                index: TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
            },
            {
                tab: TABS_ATTENDANCE.PAYROLL.TRANS,
                perm: [permissionObj?.WEB_PAYROLL_ACTIVE_READ, permissionObj?.WEB_PAYROLL_HISTORY_READ],
                index: TABS_ATTENDANCE.PAYROLL.INDEX,
            },
        ];

        const auxStep = stepConfig.reduce((acc, { tab, perm, index }) => {
            const hasPermission = DEV ||
                (index === TABS_ATTENDANCE.SUBMISSIONS.INDEX && isAttendanceSupervisor) ||
                perm.some(p => permission?.includes(p));
            if (hasPermission) {
                acc.push({ label: t(tab), index: index });
                if (activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);

        if (activeTab !== null && !currentTab) handleStepChange(activeTab);
    }, [t, permission]);

    useEffect(() => {
        setSelectForDelete([])
    }, [activeStep, view, subView]);

    const handleView = (view) => {
        clear();
        setView(view);
    }

    const handleSubView = (step) => {
        if (currentSubView != null) {
            setSubView(currentSubView);
            handleModuleOption(step, currentSubView);
            return;
        }

        switch (step) {
            case TABS_ATTENDANCE.ATTENDANCE.INDEX: {
                setSubView(null);
                handleModuleOption(step, null);
                break;
            }
            case TABS_ATTENDANCE.MANAGE.INDEX: {
                const allowedSubView = MANAGE_SUBVIEWS_BY_PERMISSION.find(
                    ({ permission: perm }) => DEV || permission?.includes(perm),
                );
                setSubView(allowedSubView?.subView ?? null);
                handleModuleOption(step, allowedSubView?.subView);
                break;
            }
            case TABS_ATTENDANCE.SUBMISSIONS.INDEX: {
                const allowedSubView = SUBMISSIONS_SUBVIEWS_BY_PERMISSION.find(
                    ({ permission: perm, subView }) =>
                        DEV ||
                        (subView === ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW && isAttendanceSupervisor) ||
                        permission?.includes(perm)
                );
                setSubView(allowedSubView?.subView ?? null);
                handleModuleOption(step, allowedSubView?.subView);
                break;
            }
            case TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX: {
                setSubView(ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL);
                handleModuleOption(step, ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL);
                break;
            }
            case TABS_ATTENDANCE.PAYROLL.INDEX: {
                // Default to the first sub-view the user is allowed to read.
                const canReadActive = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_ACTIVE_READ);
                const payrollSubView = canReadActive
                    ? ATTENDANCE_SUBVIEW_MODEL.PAYROLL.ACTIVE
                    : ATTENDANCE_SUBVIEW_MODEL.PAYROLL.HISTORY;
                setSubView(payrollSubView);
                handleModuleOption(step, payrollSubView);
                break;
            }
            default:
                setSubView(null);
                break;
        }
    };

    const handleModuleOption = (step, subView) => {
        switch (step) {
            case TABS_ATTENDANCE.ATTENDANCE.INDEX: {
                setModuleId(MODELS.Attendance);
                setOption(TABLES.ATTENDANCE);
                break;
            }
            case TABS_ATTENDANCE.SUBMISSIONS.INDEX: {
                setModuleId(null);
                if(subView === ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW){
                    setOption(TABLES.ATTENDANCE_SUBMISSION_PERIOD_REVIEW);
                }else if(subView === ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED){
                    setOption(TABLES.ATTENDANCE_SUBMISSION_REQUEST);
                }else if(subView === ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY){
                    setOption(TABLES.ATTENDANCE_SUBMISSION_HISTORY);
                }else {
                    setOption(null);
                }
                break;
            }
            case TABS_ATTENDANCE.MANAGE.INDEX: {
                if(subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS){
                    setModuleId(MODELS.ATTENDANCE_SHIFT);
                    setOption(TABLES.ATTENDANCE_SHIFT);
                }else if(subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.SPECIAL_PERMISSION){
                    setModuleId(null);
                    setOption(null);
                    if(permission.includes(permissionObj.WEB_USER_SPECIAL_PERMISSION_ASSIGN)){
                        setSubViewChild(SPECIAL_PERMISSION_TAB.ADD_EMPLOYEES.key);
                    }else{
                        setSubViewChild(SPECIAL_PERMISSION_TAB.ASSIGNED_EMPLOYEES.key);
                    }
                }else if(subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY){
                    setModuleId(null);
                    setOption(null);
                    if(permission.includes(permissionObj.WEB_PAYROLL_DEDUCTION_POLICY_READ)){
                        setSubViewChild(DEDUCTION_POLICY_TAB.GLOBAL.key);
                    }else{
                        setSubViewChild(DEDUCTION_POLICY_TAB.USER.key);
                    }
                }
                break;
            }
            case TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX: {
                setModuleId(MODELS.PAYROLL_POLICY);
                if(subView === ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL){
                    setOption(TABLES.PAYROLL_POLICY_GLOBAL);
                }else if(subView === ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.BY_DEPARTMENT){
                    setOption(TABLES.PAYROLL_POLICY_GROUP);
                }
                break;
            }
            case TABS_ATTENDANCE.PAYROLL.INDEX: {
                applyPayrollModuleOption(subView);
                break;
            }
            default: {
                setModuleId(null);
                setOption(null);
                break;
            }
        }
    };

    /**
     * Sets the export option/module for the given payroll sub-view (active vs. history).
     */
    const applyPayrollModuleOption = (view) => {
        const isHistory = view === ATTENDANCE_SUBVIEW_MODEL.PAYROLL.HISTORY;
        setModuleId(isHistory ? MODELS.PAYROLL_HISTORY : MODELS.PAYROLL_ACTIVE);
        setOption(isHistory ? TABLES.PAYROLL_HISTORY : TABLES.PAYROLL_ACTIVE);
    };

    /**
     * The index of the active tab is stored in the context, so that the user can return to the same tab in case the view is redirected.
     * @param step
     */
    const handleStepChange = (step) => {
        clear();
        setActiveStep(step);
        setSelectedOption(EmailFrequency.DAILY.id);
        // The tab keys were already applied, so the state is dropped. The table filters
        // survive: this runs on mount, before those tables are rendered, and each table
        // clears its own key once restored.
        const { payrollHistoryFilters, submissionHistoryFilters, periodReviewFilters } = location?.state || {};
        const keptState = {};
        if (payrollHistoryFilters) keptState.payrollHistoryFilters = payrollHistoryFilters;
        if (submissionHistoryFilters) keptState.submissionHistoryFilters = submissionHistoryFilters;
        if (periodReviewFilters) keptState.periodReviewFilters = periodReviewFilters;
        navigate(location.pathname, { replace: true, state: keptState });
        handleSubView(step);
    };

    const clear = () => {
        setSearchElement('');
        setFinalArray([]);
        setSearch(false);
    }
    /**
     * Function to validate permissions for the create and delete buttons. It can also be applied to import, export, and search buttons.
     * If no permission is assigned or the tabIndex variable from the Context is null, the button is import { ACTION_MODEL } from './../../../../Helpers/ActionModel';
     not displayed.
     * @param array list: List of permissions based on the active tab.
     * @returns {boolean|*}
     */
    const permissionButtons = (list) => {
        if(activeStep == null){
            return false;
        }
        const permissionRequired = list[activeStep];

        if (!permissionRequired) {
            return false;
        }
        return DEV ||
            (isAttendanceSupervisor && subView === ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW)  ||
            permission?.includes(permissionRequired);
    };

    const goCreate = () => {
        if (!TABS_ATTENDANCE.MANAGE) return;

        if(subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES ||
            subView === ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL ||
            subView === ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.BY_DEPARTMENT ||
            subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY){
            handleCreate();
            return;
        }

        const routeBySubView = {
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS]: routes.attendanceShiftAccess.add,
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.SETTINGS]: "",
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES]: "",
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.GEOFENCE]: routes.attendanceGeofence.create,
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW]:"",
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUEST]: "",
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY]: "",
        };

        const route = routeBySubView[subView];
        if (route) {
            navigate(route, {
                state: {
                    currentTab: activeStep,
                    currentSubView: subView,
                },
            });
        }
    };


    const changeSubViewManage = (value) => {
        setSearchElement('');
        handleModuleOption(activeStep, value);
        setSubView(value)
    }

    const registerAction = (key, actions) => {
        actionsRef.current[key] = actions;
        setActionsMap(prev => ({
            ...prev,
            [key]: actions
        }));
    };

    const handleDelete = () => {
        actionsRef.current?.[subView]?.handleDelete();
    };

    const handleUpdate = () => {
        actionsRef.current?.[subView]?.handleUpdate();
    };

    const handleAssign = () => {
        actionsRef.current?.[subView]?.handleAssign();
    };

    const handleReviewUpdates = () => {
        actionsRef.current?.[subView]?.handleReviewUpdates();
    };

    const handleCreate = () => {
        actionsRef.current?.[subView]?.handleCreate();
    };

    const canReviewUpdates = () => !!actionsMap?.[subView]?.handleReviewUpdates;

    return {
        searchElement,
        setSearchElement,
        option,
        selectedOption,
        setSimpleSearch,
        setSearch,
        contractorId,
        supplierId,
        search,
        setSendSimpleSearch,
        searchClean,
        criteria,
        setCriteria,
        filterDialogShow,
        setSelectedOption,
        finalArray,
        moduleId,
        subView,
        activeStep,
        setActiveStep,
        steps,
        setFilterDialogShow,
        setFinalArray,
        handleStepChange,
        view,
        setView,
        render,
        setRender,
        permissionButtons,
        handleUpdate,
        bodyForExport,
        setBodyForExport,
        changeSubViewManage,
        goCreate,
        selectForDelete,
        setSelectForDelete,
        registerAction,
        handleDelete,
        modified,
        setModified,
        handleView,
        DEV,
        permission,
        navigate,
        selectedList,
        setSelectedList,

        actionsRef,
        canReviewUpdates,
        handleReviewUpdates,
        isAllChecked,
        setIsAllChecked,
        subViewChild,
        setSubViewChild,
        handleAssign,
        isAttendanceSupervisor,
        exportReady,
        setExportReady
    };
};

export default PayrollFunctionality;
